import { MicVAD } from "@ricky0123/vad-web";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { dismissBanner, showBanner } from "../slices/bannerSlice";
import type { AppDispatch } from "../store";
import { EventType, trackEvent } from "../utils/analytics";
import { useAppSelector } from "./redux-hooks";

const NO_SPEECH_TIMEOUT_MIC_ONLY_MS = 30_000;
const NO_SPEECH_TIMEOUT_HEADPHONES_MS = 45_000;
const BANNER_ID = "no-speech-detected";
const BANNER_MESSAGE_MIC_ONLY =
  "We're not picking up any audio from your microphone. Please check that it isn't muted and your input volume is high enough.";
const BANNER_MESSAGE_HEADPHONES =
  "We're not picking up any audio. Please make sure the right browser tab is shared with audio enabled, and that your microphone isn't muted.";

// Thresholds raised above library defaults (0.3 / 0.25) — defaults trip on
// HVAC, breathing, and keyboard noise, which would reset the silence timer
// and prevent the 30s banner from ever firing.
// Loaded from jsdelivr rather than self-hosted: prod serves the app from a GCS
// bucket behind a load balancer whose URL map only routes /static/* to the
// bucket, so /vad/* fell through to the SPA default and returned index.html.
// Versions are pinned to the installed @ricky0123/vad-web and onnxruntime-web.
// VAD only drives the no-speech banner and fails silently (see .catch below),
// so on networks that block the CDN the banner simply won't show.
const VAD_OPTIONS = {
  baseAssetPath: "https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.30/dist/",
  onnxWASMBasePath: "https://cdn.jsdelivr.net/npm/onnxruntime-web@1.26.0/dist/",
  model: "v5" as const,
  positiveSpeechThreshold: 0.5,
  negativeSpeechThreshold: 0.35,
};

type VADInstance = Awaited<ReturnType<typeof MicVAD.new>>;

export function useNoSpeechDetection(
  isRecording: boolean,
  isPaused: boolean,
  mediaRecorder: MediaRecorder | null,
  withHeadphones = false,
) {
  const dispatch = useDispatch<AppDispatch>();
  const bannerVisible = useAppSelector((s) => s.banner.banners.some((b) => b.id === BANNER_ID));

  const vadRef = useRef<VADInstance | null>(null);
  const monitoredStreamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasAlertedRef = useRef(false);
  const userDismissedRef = useRef(false);
  const withHeadphonesRef = useRef(withHeadphones);
  withHeadphonesRef.current = withHeadphones;

  const clearSilenceTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const showAlert = useCallback(() => {
    dispatch(
      showBanner({
        id: BANNER_ID,
        type: "warning",
        message: withHeadphonesRef.current ? BANNER_MESSAGE_HEADPHONES : BANNER_MESSAGE_MIC_ONLY,
        closable: true,
      }),
    );
  }, [dispatch]);

  const hideAlert = useCallback(() => {
    dispatch(dismissBanner(BANNER_ID));
  }, [dispatch]);

  const armSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    if (hasAlertedRef.current || userDismissedRef.current) return;
    const timeoutMs = withHeadphonesRef.current ? NO_SPEECH_TIMEOUT_HEADPHONES_MS : NO_SPEECH_TIMEOUT_MIC_ONLY_MS;
    timerRef.current = setTimeout(() => {
      hasAlertedRef.current = true;
      showAlert();
      trackEvent(EventType.NO_SPEECH_DETECTED, {
        with_headphones: withHeadphonesRef.current,
        timeout_ms: timeoutMs,
      });
    }, timeoutMs);
  }, [clearSilenceTimer, showAlert]);

  const destroyVAD = useCallback(() => {
    if (vadRef.current) {
      vadRef.current.destroy();
      vadRef.current = null;
    }
    monitoredStreamRef.current = null;
    clearSilenceTimer();
  }, [clearSilenceTimer]);

  useEffect(() => {
    const stream = mediaRecorder?.stream;

    if (!isRecording || isPaused || !stream?.active) {
      return;
    }

    // iOS fallback rebuilds mediaRecorder with a raw stream instead of the
    // AudioContext destination — when the MediaStream identity changes, tear
    // down the old VAD and re-init against the new stream.
    if (vadRef.current && monitoredStreamRef.current !== stream) {
      vadRef.current.destroy();
      vadRef.current = null;
      monitoredStreamRef.current = null;
    }

    if (vadRef.current) {
      vadRef.current.start();
      armSilenceTimer();
      return;
    }

    // Scoped to this effect invocation: cleanup flips it before the next
    // run (or unmount), so a stale MicVAD.new() resolution destroys itself
    // instead of overwriting a freshly-created instance from the next run.
    let cancelled = false;

    MicVAD.new({
      ...VAD_OPTIONS,
      getStream: async () => stream,
      pauseStream: async () => {},
      resumeStream: async () => stream,
      onSpeechStart: () => {
        clearSilenceTimer();
        if (hasAlertedRef.current) {
          hasAlertedRef.current = false;
          hideAlert();
        }
      },
      onSpeechEnd: () => {
        armSilenceTimer();
      },
      onVADMisfire: () => {
        // Brief noise (cough, chair scrape) — do NOT reset the silence timer.
      },
    })
      .then((vad) => {
        if (cancelled) {
          vad.destroy();
          return;
        }
        vadRef.current = vad;
        monitoredStreamRef.current = stream;
        vad.start();
        armSilenceTimer();
      })
      .catch((err) => {
        Sentry.captureException(err, {
          tags: { feature: "vad_no_speech_detection" },
          extra: { with_headphones: withHeadphonesRef.current },
        });
      });

    return () => {
      cancelled = true;
    };
  }, [isRecording, isPaused, mediaRecorder, armSilenceTimer, clearSilenceTimer, hideAlert]);

  useEffect(() => {
    if (isPaused && vadRef.current) {
      vadRef.current.pause();
      clearSilenceTimer();
    }
  }, [isPaused, clearSilenceTimer]);

  useEffect(() => {
    if (!isRecording && !isPaused && vadRef.current) {
      destroyVAD();
      hasAlertedRef.current = false;
      userDismissedRef.current = false;
      hideAlert();
    }
  }, [isRecording, isPaused, destroyVAD, hideAlert]);

  useEffect(() => {
    return () => {
      destroyVAD();
      hideAlert();
    };
  }, [destroyVAD, hideAlert]);

  // Sticky dismiss: if the banner disappears while we still expected it
  // showing, the user clicked X — don't re-arm this recording session.
  // Speech- and stop-driven dismissals reset hasAlertedRef before hiding,
  // so they skip this branch.
  useEffect(() => {
    if (!bannerVisible && hasAlertedRef.current) {
      userDismissedRef.current = true;
      hasAlertedRef.current = false;
    }
  }, [bannerVisible]);
}
